/* pasteRow.js -- PASTE A ROW COPIED FROM EXCEL INTO A MONTH GRID: the ONE owner of the rules (2026-10-26; moved here from
 * components/BillingTermsSection.jsx, billing terms P3 decision 6). Every month grid on the Deferred Revenue page -- and the shared
 * ManualRenewalsGrid wherever it renders -- calls handleRowPaste. Records: diffs/dr-paste-build.txt, diffs/dr-paste-excel.txt.
 *
 * THE RULES (each grid then applies the values through its OWN commit rule, exactly as if the values had been typed):
 *   - a paste is a ROW when Excel's HTML flavor holds a row of 2+ cells, or the text flavor contains a TAB; anything else (a single
 *     value) is NOT intercepted and behaves as before;
 *   - Excel's HTML flavor is read first when present: a cell's x:num value is its exact number (a cell showing "%" = x:num x 100, the
 *     same unit the text "5.3%" gives); a bare x:num or none = the cell's text. Otherwise the text flavor (tab-separated);
 *   - one row only (several non-empty rows are refused); a merged cell is refused;
 *   - $, commas, spaces (non-breaking ones too), % and (parentheses = negative) are accepted in a cell; a blank cell is "no entry"
 *     (null); an Excel accounting-format zero ("-", "$ -", "$-", with any spaces) reads 0, and its negative "$ (4,408)" reads -4408;
 *   - a cell that is not a number, or a row longer than the months left from the focused cell, is refused with NOTHING applied
 *     and a plain-English message;
 *   - a disabled cell (a view-only login: the ViewOnly fieldset) takes nothing.
 * Typing is unchanged: parseAmount (used on blur) reads neither a lone "-" nor "$ (4,408)"; only a pasted cell does.
 * Plain ASCII on purpose (CLAUDE.md 12.5 rule 55). */
import { formatMonthLabel, getYear } from '../engines/dateUtils';

export const PASTE_HINT = 'Tip: paste a row copied from Excel';
/* the "i" beside the tip (utils/PasteHint.jsx; Brian 2026-10-26): Excel copies what a cell SHOWS (diffs/dr-paste-excel.txt finding 1) */
export const PASTE_TOOLTIP = 'Before you copy from Excel: Excel copies what the cell shows. If your cells show whole dollars, the cents are dropped. '
  + 'Select the row and click Increase Decimal (Home tab) until the cents show, or set the format to General, then copy. '
  + 'Zeros shown as "-" and negatives in (brackets) paste fine.';
export const PASTE_HINT_STYLE = { fontSize: 9, color: '#8B8FA3', fontStyle: 'italic', marginBottom: 6 };
export const PASTE_ERROR_STYLE = { fontSize: 10, color: '#C0392B', marginTop: 4 };

/* a money / percent typed or pasted: optional $, commas, spaces, %, (parentheses) for a negative. '' -> null; not a number -> NaN. */
export function parseAmount(text) {
  const s = String(text === undefined || text === null ? '' : text).trim();
  if (s === '') return null;
  const neg = /^\(.*\)$/.test(s);
  const t = s.replace(/^\((.*)\)$/, '$1').replace(/[$,%\s]/g, '');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(t)) return NaN;
  const n = parseFloat(t);
  return neg ? -n : n;
}

/* a PASTED cell: an Excel accounting-format zero ("-", "$ -", "$-"; any spaces, U+00A0 included) reads 0, and an accounting-format
 * negative with the $ outside the parentheses ("$ (4,408)") reads as "(4,408)"; otherwise parseAmount. */
const ACCOUNTING_ZERO = /^\$?\s*-$/;
export function parsePastedCell(text) {
  const s = String(text === undefined || text === null ? '' : text).replace(/\u00a0/g, ' ').trim();
  if (ACCOUNTING_ZERO.test(s)) return 0;
  return parseAmount(s.replace(/^\$\s*(\(.*\))$/, '$1'));
}

const notANumber = (i, cell) => ({ error: 'Not pasted: cell ' + (i + 1) + ' ("' + String(cell).replace(/\u00a0/g, ' ').trim() + '") is not a number -- nothing was changed.' });
const oneRowOnly = n => ({ error: n ? 'Paste one row at a time (the clipboard holds ' + n + ' rows).' : 'Nothing to paste.' });
const cents = v => (v === null ? null : Math.round(v * 100) / 100);

/* one copied row (the text flavor) -> { values } (a number rounded to the cent, or null for a blank cell) or { error } */
export function parsePastedRow(text) {
  const lines = String(text || '').split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length !== 1) return oneRowOnly(lines.length);
  const cells = lines[0].split('\t');
  const values = [];
  for (let i = 0; i < cells.length; i++) {
    const v = parsePastedCell(cells[i]);
    if (v !== null && !Number.isFinite(v)) return notANumber(i, cells[i]);
    values.push(cents(v));
  }
  return { values };
}

/* Excel's HTML flavor -> { rows: [[{ text, num }]], merged } or null when the HTML is not Excel's (then the text flavor is used).
 * Excel's = its namespace, its ProgId, or an x:num attribute. A row whose cells are all blank is dropped (as a blank text line is). */
const EXCEL_HTML = /urn:schemas-microsoft-com:office:excel|content=["']?Excel\.Sheet|\bx:num\b/i;
export function readExcelHtml(html) {
  const h = String(html || '');
  if (!EXCEL_HTML.test(h) || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(/<table[\s>]/i.test(h) ? h : '<table>' + h + '</table>', 'text/html');
  let merged = false;
  const rows = [...doc.querySelectorAll('tr')].map(tr => [...tr.querySelectorAll('td,th')].map(td => {
    if (Number(td.getAttribute('colspan') || 1) > 1 || Number(td.getAttribute('rowspan') || 1) > 1) merged = true;
    const raw = td.getAttribute('x:num');
    return { text: td.textContent || '', num: raw === null || raw.trim() === '' ? null : raw.trim() };
  })).filter(cells => cells.some(c => c.num !== null || c.text.replace(/\u00a0/g, ' ').trim() !== ''));
  return { rows, merged };
}

/* one Excel HTML row -> { values } or { error } (the same rules and messages as the text flavor) */
function parseHtmlRow(x) {
  if (x.rows.length !== 1) return oneRowOnly(x.rows.length);
  if (x.merged) return { error: 'Not pasted: the copied row has merged cells -- nothing was changed.' };
  const values = [];
  for (let i = 0; i < x.rows[0].length; i++) {
    const { text, num } = x.rows[0][i];
    let v;
    if (num !== null) { v = Number(num); if (Number.isFinite(v) && /%/.test(text)) v = v * 100; }
    else v = parsePastedCell(text);
    if (v !== null && !Number.isFinite(v)) return notANumber(i, num !== null ? num : text);
    values.push(cents(v));
  }
  return { values };
}

/* the clipboard as handed to a paste: a string (the text flavor) or { html, text } */
const asClip = clip => (clip && typeof clip === 'object' ? { html: clip.html || '', text: clip.text || '' } : { html: '', text: String(clip || '') });

/* is this clipboard a row? Excel's HTML with a row of 2+ cells, or text with a tab. A single value is left to the browser. */
export function isRowPaste(clip) {
  const { html, text } = asClip(clip);
  const x = readExcelHtml(html);
  if (x && x.rows.some(r => r.length >= 2)) return true;
  return /\t/.test(text);
}

/* the clipboard's row: Excel's HTML when present, else the text flavor */
export function parseClipboardRow(clip) {
  const { html, text } = asClip(clip);
  const x = readExcelHtml(html);
  return x && x.rows.length ? parseHtmlRow(x) : parsePastedRow(text);
}

/* the row placed from `month` onward: { cells: [[month, value|null], ...] } or { error } */
export function planRowPaste(clip, months, month) {
  const r = parseClipboardRow(clip);
  if (r.error) return r;
  const start = (months || []).indexOf(month);
  if (start < 0) return { error: 'Not pasted: this cell is not one of the grid\'s months -- nothing was changed.' };
  if (start + r.values.length > months.length) return { error: 'Not pasted: the row has ' + r.values.length + ' values but only ' + (months.length - start) + ' month(s) remain from ' + formatMonthLabel(month, 'short') + ' ' + getYear(month) + ' -- nothing was changed.' };
  return { cells: r.values.map((v, i) => [months[start + i], v]) };
}

/* the onPaste handler every month grid uses. apply(cells) receives [[month, number|null], ...] and commits them through the grid's own
 * rule; onError(message|null) shows or clears the grid's message. Returns nothing; a single value is not intercepted. */
export function handleRowPaste(e, { months, month, apply, onError }) {
  const el = e.target;
  if (el && (el.disabled || (typeof el.matches === 'function' && el.matches(':disabled')))) { e.preventDefault(); return; }   // view only
  const cd = e.clipboardData;
  const clip = { html: cd ? cd.getData('text/html') : '', text: cd ? cd.getData('text') : '' };
  if (!isRowPaste(clip)) return;
  e.preventDefault();
  const p = planRowPaste(clip, months, month);
  if (p.error) { onError(p.error); return; }
  onError(null);
  apply(p.cells);
}
